import React, { useState } from 'react';
import { css, keyframes } from '@emotion/react';
import { Button, Tooltip } from 'antd';
import useIsClinician from 'vl-common/src/hooks/useIsClinician';

const pulse = keyframes`
  0% {
    filter: opacity(0);
  }
  50% {
    filter: opacity(50%);
    border: 4px solid var(--productPrimary);
  }
  100% {
    filter: opacity(0%);
  }
`;

const pulsateAnimationStyle = css`
  &::before {
    content: '';
    position: absolute;
    top: -3.5px;
    left: -3.5px;
    right: -3.5px;
    bottom: -3.5px;
    border-radius: 50%;
    animation-name: ${pulse};
    animation-duration: 2s;
    animation-iteration-count: 3;
    animation-fill-mode: forwards;
  }
`;

export const VideoMenuButton = ({
  title,
  disabled = false,
  onClick,
  icon,
  pulsateOnCallStart,
  'data-testid': dataTestid = ''
}: {
  title: string;
  disabled?: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  pulsateOnCallStart?: boolean;
  'data-testid': string;
}) => {
  const isClinician = useIsClinician();
  const [hasPulsatingEnded, setHasPulsatingEnded] = useState(isClinician || !pulsateOnCallStart);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Escape') setIsTooltipOpen(false);
  };

  return (
    <Tooltip
      title={<span aria-hidden={true}>{title}</span>}
      open={isTooltipOpen}
      onOpenChange={(newOpen: boolean) => setIsTooltipOpen(newOpen)}
      trigger={['hover', 'focus']}
    >
      <Button
        className="toggleButton"
        type="primary"
        shape="circle"
        disabled={disabled}
        icon={icon}
        onKeyDown={handleKeyDown}
        onClick={onClick}
        onAnimationEnd={() => setHasPulsatingEnded(true)}
        aria-label={title}
        data-testid={dataTestid}
        css={css`
          // High Contrast Fix
          body[data-contrast='high'] & {
            & svg path[fill='none'] {
              fill: none !important;
            }
          }
          ${!isClinician && pulsateOnCallStart && pulsateAnimationStyle}
          box-shadow: ${hasPulsatingEnded ? '0 3px 3px rgba(0, 0, 0, 0.5)' : 'none'};
          & > * {
            font-size: 20px;
            margin: 0 auto;
            display: block;
            padding: 0;
          }

          body:not([data-contrast='high']) &:focus-visible {
            outline: 2px solid var(--productGray) !important;
            outline-offset: 2px !important;
          }
        `}
      />
    </Tooltip>
  );
};
